import { useState } from "react";
import { updateUserAttributes, updatePassword } from "aws-amplify/auth";
import { useAuth } from "../auth/provider";
import { useMerchantStore } from "../store/merchant";
import { Button } from "../components/shared/button";
import { AppBar } from "../components/shared/app-bar";
import { MfaSection } from "../components/profile/mfa-section";
import "./profile.css";

function PasswordRule({ label, met }: { label: string; met: boolean }) {
  return (
    <li
      data-met={met}
      style={{
        listStyle: "none",
        fontSize: "12px",
        color: met ? "var(--green)" : "var(--ink3)",
        marginBottom: "4px",
      }}
    >
      {met ? "✓" : "○"} {label}
    </li>
  );
}

export function ProfilePage() {
  const { user, refreshUser } = useAuth();
  const businessName = useMerchantStore((s) => s.businessName);

  const [given, setGiven] = useState(user?.givenName ?? "");
  const [family, setFamily] = useState(user?.familyName ?? "");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);

  const [currentPw, setCurrentPw] = useState("");
  const [newPw, setNewPw] = useState("");
  const [confirmPw, setConfirmPw] = useState("");
  const [pwSaving, setPwSaving] = useState(false);
  const [pwError, setPwError] = useState<string | null>(null);
  const [pwSuccess, setPwSuccess] = useState(false);

  const rules = {
    length: newPw.length >= 8,
    lower: /[a-z]/.test(newPw),
    upper: /[A-Z]/.test(newPw),
    digit: /[0-9]/.test(newPw),
  };
  const allRulesMet = Object.values(rules).every(Boolean);
  const canSubmit =
    currentPw.length > 0 &&
    allRulesMet &&
    newPw === confirmPw &&
    !pwSaving;

  if (!user) return null;

  async function savePersonal() {
    setSaving(true);
    setError(null);
    setSuccess(false);
    try {
      await updateUserAttributes({
        userAttributes: { given_name: given, family_name: family },
      });
      setSuccess(true);
      await refreshUser();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to save");
    } finally {
      setSaving(false);
    }
  }

  async function changePassword() {
    setPwSaving(true);
    setPwError(null);
    setPwSuccess(false);
    try {
      await updatePassword({ oldPassword: currentPw, newPassword: newPw });
      setPwSuccess(true);
      setCurrentPw("");
      setNewPw("");
      setConfirmPw("");
    } catch (err) {
      setPwError(err instanceof Error ? err.message : "Failed to change password");
    } finally {
      setPwSaving(false);
    }
  }

  return (
    <>
      <AppBar variant="subpage" backTo="/dashboard" backLabel="Dashboard" />
      <div className="profile-wrap">
        <h1 className="profile-title">Profile</h1>

        <div className="card">
          <div className="clabel">Personal details</div>
        {error && <div className="profile-error">{error}</div>}
        {success && (
          <div className="profile-success">Personal details saved.</div>
        )}
        <div className="profile-field">
          <label className="profile-label" htmlFor="profile-first">
            First name
          </label>
          <input
            id="profile-first"
            className="profile-input"
            value={given}
            onChange={(e) => setGiven(e.target.value)}
          />
        </div>
        <div className="profile-field">
          <label className="profile-label" htmlFor="profile-last">
            Last name
          </label>
          <input
            id="profile-last"
            className="profile-input"
            value={family}
            onChange={(e) => setFamily(e.target.value)}
          />
        </div>
        <Button
          variant="gradient"
          onClick={savePersonal}
          disabled={saving}
        >
          {saving ? "Saving…" : "Save personal details"}
        </Button>
      </div>

      <div className="card">
        <div className="clabel">Business</div>
        <div className="profile-field">
          <label className="profile-label" htmlFor="profile-biz">
            Business name
          </label>
          <input
            id="profile-biz"
            className="profile-input"
            value={businessName ?? ""}
            readOnly
          />
          <div className="profile-helper">
            Managed in your Square account.
          </div>
        </div>
      </div>

      <div className="card">
        <div className="clabel">Change password</div>
        {pwError && <div className="profile-error">{pwError}</div>}
        {pwSuccess && (
          <div className="profile-success">Password changed.</div>
        )}
        <div className="profile-field">
          <label className="profile-label" htmlFor="pw-current">
            Current password
          </label>
          <input
            id="pw-current"
            className="profile-input"
            type="password"
            value={currentPw}
            onChange={(e) => setCurrentPw(e.target.value)}
            autoComplete="current-password"
          />
        </div>
        <div className="profile-field">
          <label className="profile-label" htmlFor="pw-new">
            New password
          </label>
          <input
            id="pw-new"
            className="profile-input"
            type="password"
            value={newPw}
            onChange={(e) => setNewPw(e.target.value)}
            autoComplete="new-password"
          />
          <ul style={{ marginTop: "8px", padding: 0 }}>
            <PasswordRule label="At least 8 characters" met={rules.length} />
            <PasswordRule label="One lowercase letter" met={rules.lower} />
            <PasswordRule label="One uppercase letter" met={rules.upper} />
            <PasswordRule label="One number" met={rules.digit} />
          </ul>
        </div>
        <div className="profile-field">
          <label className="profile-label" htmlFor="pw-confirm">
            Confirm new password
          </label>
          <input
            id="pw-confirm"
            className="profile-input"
            type="password"
            value={confirmPw}
            onChange={(e) => setConfirmPw(e.target.value)}
            autoComplete="new-password"
          />
          {confirmPw.length > 0 && newPw !== confirmPw && (
            <div
              style={{
                fontSize: "12px",
                color: "var(--red)",
                marginTop: "4px",
              }}
            >
              Passwords do not match.
            </div>
          )}
        </div>
        <Button
          variant="gradient"
          onClick={changePassword}
          disabled={!canSubmit}
        >
          {pwSaving ? "Changing…" : "Change password"}
        </Button>
      </div>

      <MfaSection />
      </div>
    </>
  );
}
